import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { useEmailContext } from '../../../contexts/EmailContext';
import { extractEmailData, getCustomerDisplayName } from '../../../utils/emailContactHelpers';
import { EmailMessage } from '../../../models/Email';
import { fetchEmailThread, ThreadContact } from '../../../services/fetchEmailThread';
import { fetchEmailContent, EmailContentTransfer } from '../../../services/fetchEmailContent';
import { EMAIL_INITIATION_METHODS } from '../../../constants/email';
import { useEnrichEmailWithContent } from './useEnrichEmailWithContent';
import { useFetchNewerThreadContacts } from './useFetchNewerThreadContacts';

export function findTransferContactIds(contacts: ThreadContact[], activeContactId: string): string[] {
  const byId = new Map(contacts.map(c => [c.contactId, c]));
  let current = byId.get(activeContactId);
  const transferContactIds: string[] = [];

  while (current) {
    if (current.initiationMethod.toUpperCase() === EMAIL_INITIATION_METHODS.TRANSFER) {
      transferContactIds.push(current.contactId);
    }
    const previousId = current.previousContactId ?? current.relatedContactId;
    current = previousId ? byId.get(previousId) : undefined;
  }

  return transferContactIds;
}

export function filterThreadContacts(
  contacts: ThreadContact[],
  activeContactId: string
): { filtered: ThreadContact[]; wasTransferred: boolean; transferContactIds: string[] } {
  const previousContactIdsSet = new Set(contacts.map(c => c.previousContactId).filter(Boolean));
  const filtered = contacts.filter(c => c.contactId === activeContactId || !previousContactIdsSet.has(c.contactId));
  const transferContactIds = findTransferContactIds(contacts, activeContactId);
  return { filtered, wasTransferred: transferContactIds.length > 0, transferContactIds };
}

interface UseEmailContactHandlersProps {
  onError?: (message: string) => void;
}

export function useEmailContactHandlers({ onError }: UseEmailContactHandlersProps): void {
  const { updateEmailData, updateEmailThread, startLoadingThread, stopLoadingThread } = useEmailContext();
  const enrichEmailWithContent = useEnrichEmailWithContent();
  const fetchNewerThreadContacts = useFetchNewerThreadContacts(onError);
  const isInitializedRef = useRef(false);
  const onErrorRef = useRef(onError);
  const enrichEmailWithContentRef = useRef(enrichEmailWithContent);
  const fetchNewerThreadContactsRef = useRef(fetchNewerThreadContacts);

  onErrorRef.current = onError;
  enrichEmailWithContentRef.current = enrichEmailWithContent;
  fetchNewerThreadContactsRef.current = fetchNewerThreadContacts;

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    const handleExtractEmailData = (contact: connect.Contact): void => {
      updateEmailData(contact.getContactId(), extractEmailData(contact));
    };

    const handleFetchEmailThread = async (contact: connect.Contact): Promise<void> => {
      const activeContactId = contact.getContactId();
      const contactAssociationId = contact.getContactAssociationId();
      if (!contactAssociationId) return;

      try {
        startLoadingThread(activeContactId);

        const [contacts, activeContent] = await Promise.all([
          fetchEmailThread(contactAssociationId),
          fetchEmailContent(activeContactId).catch(error => {
            console.error('Failed to fetch active email content:', error);
            onErrorRef.current?.('Failed to load the email content. Please try again.');
            return undefined;
          })
        ]);

        const { filtered: filteredContacts, transferContactIds } = filterThreadContacts(contacts, activeContactId);
        const customerDisplayName = getCustomerDisplayName(contact);

        const newerContentByContactId = await fetchNewerThreadContactsRef.current(filteredContacts, activeContactId);

        const transferEntries = await Promise.all(
          transferContactIds.map(async contactId => {
            const details =
              contactId === activeContactId
                ? (activeContent?.transferInfo ?? undefined)
                : ((await fetchEmailContent(contactId).catch(() => null))?.transferInfo ?? undefined);
            return details ? ([contactId, details] as const) : undefined;
          })
        );
        const transfersByContactId = new Map(
          transferEntries.filter((entry): entry is [string, EmailContentTransfer] => !!entry)
        );

        const messages: EmailMessage[] = filteredContacts
          .map(
            ({ contactId, initiationTimestamp }): EmailMessage => ({
              contactId,
              to: [],
              cc: [],
              timestamp: new Date(initiationTimestamp).getTime()
            })
          )
          .map(message => {
            if (message.contactId === activeContactId && activeContent) {
              return enrichEmailWithContentRef.current(message, activeContent);
            }
            const newerContent = newerContentByContactId.get(message.contactId);
            return newerContent ? enrichEmailWithContentRef.current(message, newerContent) : message;
          })
          .map(message =>
            message.contactId === activeContactId && message.from && customerDisplayName
              ? { ...message, from: { ...message.from, displayName: customerDisplayName } }
              : message
          )
          .map(message => {
            const transferInfo = transfersByContactId.get(message.contactId);
            return transferInfo ? { ...message, transferInfo } : message;
          })
          .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

        // Each transfer's comment is accurate on its own (the backend scopes it per transfer,
        // no leaking between them), but only the most recent transfer's comment is still
        // actionable for the agent - older ones are superseded, so we hide them here.
        const lastTransferIndex = messages.reduce(
          (lastIndex, message, index) => (message.transferInfo ? index : lastIndex),
          -1
        );
        const messagesWithOnlyTheLatestComment = messages.map((message, index) =>
          message.transferInfo && index !== lastTransferIndex
            ? { ...message, transferInfo: { ...message.transferInfo, comment: '' } }
            : message
        );

        updateEmailThread(activeContactId, { contactAssociationId, messages: messagesWithOnlyTheLatestComment });
      } catch (error) {
        console.error('Failed to fetch email thread:', error);
        onErrorRef.current?.('Failed to load the email thread. Please try again.');
      } finally {
        stopLoadingThread(activeContactId);
      }
    };

    // When the page loads, Streams tells us about each open contact twice: once in connect.agent
    // and once in connect.contact. Set up twice, a contact gets two onConnected listeners and loads
    // its email twice. So we keep the ids we have set up and skip repeats. When Streams drops a
    // contact, it drops its listeners too. So we forget the id then, and the contact can be set up again.
    const handledContactIds = new Set<string>();

    const handleEmailContact = (contact: connect.Contact): void => {
      const contactId = contact.getContactId();
      if (handledContactIds.has(contactId)) return;
      handledContactIds.add(contactId);
      contact.onDestroy(() => handledContactIds.delete(contactId));

      handleExtractEmailData(contact);

      contact.onConnected(() => {
        handleExtractEmailData(contact);
        void handleFetchEmailThread(contact);
      });
    };

    connect.agent(agent => {
      agent
        .getContacts()
        .filter(c => (c.getType() as string) === connect.ContactType.EMAIL)
        .filter(c => c.getInitiationMethod()?.toUpperCase() !== EMAIL_INITIATION_METHODS.AGENT_REPLY)
        .forEach(handleEmailContact);
    });

    connect.contact(contact => {
      if ((contact.getType() as string) !== connect.ContactType.EMAIL) {
        return;
      }

      const initiationMethod = contact.getInitiationMethod() as string;

      if (initiationMethod?.toUpperCase() === EMAIL_INITIATION_METHODS.AGENT_REPLY) {
        return;
      }

      handleEmailContact(contact);
    });
  }, [startLoadingThread, stopLoadingThread, updateEmailData, updateEmailThread]);
}
